import React, {useState} from 'react';
import {ScrollableFlex, WrapperFlex} from '../utils/scrolling';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {Flex} from '@chakra-ui/react';
import {useOnboardingContext} from './Onboarding';
import {
  useIntegrations,
  useUpdateIntegration,
} from '../../hooks/api/integrations';
import {LinkedInIntegrationResponse} from '../../shared/api/definitions/integration';
import {getLinkedInOAuthUrl} from '../Integrations/AllIntegrations';
import {useGetAdAccounts} from '../../hooks/api/linkedin';
import Select from 'react-select';
import {defaultSelectStyles} from '../theme/select';

export const ConnectLinkedIn = () => {
  const {onPageNavigate, isFirstStep} = useOnboardingContext();
  const {data: integrations, isPending} = useIntegrations({});

  const linkedInIntegration = integrations?.find(
    (integration) => integration.type === 'linkedin'
  );

  return (
    <WrapperFlex>
      <ScrollableFlex gap={12}>
        <Flex direction="column" gap={5}>
          <Box fontSize="2xl">
            Want even more tracked insights? Connect LinkedIn Ads
          </Box>
          <Box color="kgray.300">
            Optional - to connect Keyplay to LinkedIn Ads, confirm you are an
            Account Manager in your LinkedIn Ads account, then follow the
            directions in the pop-up window.
          </Box>
        </Flex>
        <Flex direction="column" gap={5}>
          {isPending ? (
            <Spinner />
          ) : linkedInIntegration ? (
            <AuthenticatedSection integration={linkedInIntegration} />
          ) : (
            <UnauthenticatedSection />
          )}
        </Flex>
      </ScrollableFlex>

      <Divider />

      <Flex justifyContent="space-between" w="100%" px={10} py={4}>
        {!isFirstStep && (
          <Button
            colorScheme="kbuttonblue"
            onClick={() => onPageNavigate('prev')}
            variant="outline"
            w="110px"
          >
            Back
          </Button>
        )}
        <Box flexGrow={1} />
        <ButtonGroup gap={4}>
          <Button
            colorScheme="kbuttonblue"
            variant="ghost"
            onClick={() => onPageNavigate('next')}
          >
            Skip
          </Button>
          <Button
            colorScheme="kbuttonblue"
            isDisabled={
              !linkedInIntegration || !linkedInIntegration.adAccountId
            }
            onClick={() => onPageNavigate('next')}
            variant="solid"
          >
            Continue
          </Button>
        </ButtonGroup>
      </Flex>
    </WrapperFlex>
  );
};

const UnauthenticatedSection = () => {
  const [isAccountManager, setIsAccountManager] = useState(false);

  return (
    <Flex direction="column" gap={6}>
      <Checkbox
        borderRadius="md"
        border="1px solid"
        borderColor="kgray.200"
        isChecked={isAccountManager}
        onChange={(e) => setIsAccountManager(e.target.checked)}
        padding={4}
        _hover={{
          bgColor: 'kgray.100',
        }}
        _active={{
          bgColor: 'kgray.200',
        }}
      >
        I am an Account Manager in LinkedIn Ads
      </Checkbox>
      <Button
        isDisabled={!isAccountManager}
        colorScheme="kbuttonblue"
        onClick={() => location.assign(getLinkedInOAuthUrl('onboarding'))}
        variant="solid"
        w="fit-content"
      >
        Authenticate LinkedIn Ads
      </Button>
    </Flex>
  );
};

type AdAccountOption = {
  label: string;
  value: number;
  name: string;
  id: number;
};

const AuthenticatedSection = ({
  integration,
}: {
  integration: LinkedInIntegrationResponse;
}) => {
  const adAccounts = useGetAdAccounts({});
  const adAccountOptions: AdAccountOption[] = (adAccounts.data ?? []).map(
    (adAccount) => ({
      label: adAccount.name,
      value: adAccount.id,
      name: adAccount.name,
      id: adAccount.id,
    })
  );
  const updateIntegrationMutation = useUpdateIntegration();

  const formatOptionLabel = ({label, id}: AdAccountOption) => {
    return (
      <Flex alignItems="center" gap={2}>
        <Text>{label}</Text>
        <Text color="gray.500">{id}</Text>
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap={6}>
      <Box fontSize="lg" fontWeight="400">
        Select the ad account you want to connect to Keyplay.
      </Box>
      <Select
        formatOptionLabel={formatOptionLabel}
        isDisabled={updateIntegrationMutation.isPending}
        isLoading={adAccounts.isPending}
        onChange={(newValue) => {
          if (!newValue) {
            return;
          }

          updateIntegrationMutation.mutate({
            type: 'linkedin',
            adAccountId: newValue.value,
          });
        }}
        options={adAccountOptions}
        placeholder="Select an ad account"
        styles={defaultSelectStyles<AdAccountOption, false>()}
        value={adAccountOptions?.find(
          (option) => option.value === integration.adAccountId
        )}
      />
    </Flex>
  );
};
